'use client';

import { useState, type FC } from 'react';
import { useLocale, useTranslations } from 'next-intl';
import type { Payment } from '@/types/payment';
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '@/shared/ui/shadcn/dialog';
import { Drawer, DrawerContent, DrawerTitle } from '@/shared/ui/shadcn/drawer';
import { Button } from '@/shared/ui/shadcn/button';
import {
  Receipt,
  Copy,
  Check,
  CheckCircle,
  XCircle,
  Clock,
  AlertCircle,
} from 'lucide-react';
import { WalnutIcon } from '@/shared/ui/WalnutIcon';
import { formatCurrency } from '@/shared/lib/utils';
import {
  getPaymentTypeKey,
  getPaymentProviderKey,
} from '@/shared/lib/payment-formatters';
import { useBreakpoint } from '@/shared/hooks/use-media-query';

interface PaymentDetailModalProps {
  payment: Payment | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const PaymentDetailModal: FC<PaymentDetailModalProps> = ({
  payment,
  open,
  onOpenChange,
}) => {
  const t = useTranslations();
  const locale = useLocale();
  const [isCopied, setIsCopied] = useState(false);
  const { isMd } = useBreakpoint();

  if (!payment) return null;

  const isMobile = !isMd;

  // Plan label: prefer historical snapshot frozen on the payment itself —
  // see PaymentHistoryItem.tsx for rationale.
  const planSnapshot = payment.relationships?.plan?.attributes;
  const planName =
    planSnapshot?.name ??
    payment.relationships?.subscription?.relationships?.plan?.attributes?.name;
  // Billing cycle comes from Plan.interval ("month"/"year") on the historical
  // plan snapshot — Plan.type only holds the tier name (lite/pro/max) and
  // never encodes the cycle, so deriving from type always resolves to null.
  // Historical snapshot preferred over current subscription.plan because the
  // tier (and thus cycle) may have changed after the charge was made.
  const planInterval =
    planSnapshot?.interval ??
    payment.relationships?.subscription?.relationships?.plan?.attributes
      ?.interval;
  const billingPeriod: 'monthly' | 'yearly' | null =
    planInterval === 'year'
      ? 'yearly'
      : planInterval === 'month'
        ? 'monthly'
        : null;

  // Subscription period window — only present for subscription_* charges.
  const periodStart = payment.attributes.period_start;
  const periodEnd = payment.attributes.period_end;
  const formattedBillingPeriod =
    periodStart && periodEnd
      ? (() => {
          const s = new Date(periodStart);
          const e = new Date(periodEnd);
          if (isNaN(s.getTime()) || isNaN(e.getTime())) return null;
          const fmt = new Intl.DateTimeFormat(locale, {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          });
          return `${fmt.format(s)} – ${fmt.format(e)}`;
        })()
      : null;

  // Refunded amount (signed) — only displayed when partial. Fully refunded
  // payments are already conveyed by the REFUNDED status badge.
  // The modal is opened only for financial payment rows from
  // PaymentHistoryItem (chain-transfer rows skip the modal entirely), so
  // currency is non-null in practice. Coalesce to an empty string for the
  // type checker rather than asserting, so a stray transfer row that
  // somehow opened the modal degrades to "—" instead of crashing.
  const refundedAmountFormatted = payment.attributes.is_partially_refunded
    ? `−${formatCurrency(
        payment.attributes.refunded_amount / 100,
        payment.attributes.currency ?? ''
      )}`
    : null;

  const failureReason = payment.attributes.failure_reason;
  const showFailureReason =
    !!failureReason &&
    (payment.attributes.status === 'failed' ||
      payment.attributes.status === 'canceled');

  const transactionId = payment.attributes.provider_charge_id;

  // Get translated payment type. For pass_purchase rows prefer the concrete
  // server-resolved product name ("Pro Pass"); legacy pass
  // rows without it fall back to the generic localized "Pass purchase" label.
  const paymentTypeKey = getPaymentTypeKey(payment.attributes.type);
  const paymentTypeDisplay =
    payment.attributes.pass_name ?? t(paymentTypeKey);

  // Get translated provider
  const providerKey = getPaymentProviderKey(payment.attributes.provider);
  const providerLabel = t(providerKey);

  // Format date and time
  const dateToFormat =
    payment.attributes.paid_at || payment.attributes.created_at;
  const parsedDate = dateToFormat ? new Date(dateToFormat) : null;
  const isValidDate =
    parsedDate &&
    !isNaN(parsedDate.getTime()) &&
    parsedDate.getFullYear() > 1970;

  const formattedDate = isValidDate
    ? parsedDate.toLocaleDateString(locale, {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      })
    : '-';

  const formattedTime = isValidDate
    ? parsedDate.toLocaleTimeString(locale, {
        hour: 'numeric',
        minute: '2-digit',
      })
    : '-';

  // Determine icon and color based on payment type
  const getPaymentIcon = () => {
    if (payment.attributes.type === 'token_topup') {
      return <WalnutIcon className="h-8 w-[25px] text-amber-500" />;
    }
    if (
      payment.attributes.type === 'subscription_initial' ||
      payment.attributes.type === 'subscription_renewal' ||
      payment.attributes.type === 'subscription_upgrade'
    ) {
      return <Receipt className="w-8 h-8 text-primary" />;
    }
    return <Receipt className="w-8 h-8 text-muted-foreground" />;
  };

  const getPaymentIconBgColor = () => {
    if (payment.attributes.type === 'token_topup') {
      return 'bg-primary/10';
    }
    if (
      payment.attributes.type === 'subscription_initial' ||
      payment.attributes.type === 'subscription_renewal' ||
      payment.attributes.type === 'subscription_upgrade'
    ) {
      return 'bg-primary/10';
    }
    return 'bg-muted';
  };

  // Get display name for payment
  const getDisplayName = () => {
    if (
      payment.attributes.type === 'token_topup' &&
      payment.attributes.metadata?.token_amount
    ) {
      return `${payment.attributes.metadata.token_amount} ${t('paymentDetails.tokens')}`;
    }
    if (planName) {
      return `${planName} ${t('paymentDetails.plan')}`;
    }
    return paymentTypeDisplay;
  };

  // Get status icon and color
  const getStatusDisplay = () => {
    switch (payment.attributes.status) {
      case 'succeeded':
        return {
          icon: <CheckCircle className="w-4 h-4" />,
          label: t('paymentDetails.status.completed'),
          className: 'text-green-600',
        };
      case 'failed':
        return {
          icon: <XCircle className="w-4 h-4" />,
          label: t('paymentDetails.status.failed'),
          className: 'text-red-600',
        };
      case 'requires_action':
        // Distinct from pending: the user must take an explicit action in
        // their payment provider (e.g. 3DS, App Store) to finalise the charge.
        return {
          icon: <AlertCircle className="w-4 h-4" />,
          label: t('paymentDetails.status.requiresAction'),
          className: 'text-warning',
        };
      case 'pending':
      case 'processing':
        return {
          icon: <Clock className="w-4 h-4" />,
          label: t('paymentDetails.status.pending'),
          className: 'text-warning',
        };
      case 'canceled':
        return {
          icon: <XCircle className="w-4 h-4" />,
          label: t('paymentDetails.status.canceled'),
          className: 'text-gray-600',
        };
      case 'expired':
        return {
          icon: <AlertCircle className="w-4 h-4" />,
          label: t('paymentDetails.status.expired'),
          className: 'text-gray-600',
        };
      default:
        return {
          icon: <Clock className="w-4 h-4" />,
          label: payment.attributes.status,
          className: 'text-gray-600',
        };
    }
  };

  const statusDisplay = getStatusDisplay();

  const handleCopyPaymentId = async () => {
    try {
      await navigator.clipboard.writeText(payment.id);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } catch {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = payment.id;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    }
  };

  // Content shared between mobile and desktop
  const headerContent = (
    <div className="flex items-center justify-between px-4 py-3 border-b">
      <div className="w-12" />
      {isMobile ? (
        <DrawerTitle className="text-sheet-title">
          {t('paymentDetails.title')}
        </DrawerTitle>
      ) : (
        <DialogTitle className="text-sheet-title">
          {t('paymentDetails.title')}
        </DialogTitle>
      )}
      <Button
        variant="ghost"
        className="text-primary font-normal px-2"
        onClick={() => onOpenChange(false)}
      >
        {t('paymentDetails.done')}
      </Button>
    </div>
  );

  const bodyContent = (
    <div className="p-6 pb-safe">
      {/* Icon and Main Info */}
      <div className="flex flex-col items-center text-center mb-8">
        <div
          className={`w-[72px] h-[72px] rounded-full ${getPaymentIconBgColor()} flex items-center justify-center mb-4`}
        >
          {getPaymentIcon()}
        </div>
        <h2 className="text-section-title mb-1">{getDisplayName()}</h2>
        <p className="text-card-title text-muted-foreground mb-1">
          {formatCurrency(
            (payment.attributes.amount ?? 0) / 100,
            payment.attributes.currency ?? ''
          )}
        </p>
        <p className="text-section-subtitle text-muted-foreground mb-3">
          {paymentTypeDisplay}
        </p>
        <div className={`flex items-center gap-1.5 ${statusDisplay.className}`}>
          {statusDisplay.icon}
          <span className="text-list-item-value">{statusDisplay.label}</span>
        </div>
      </div>

      {/* Transaction Details */}
      <div className="mb-6">
        <h3 className="text-section-eyebrow text-muted-foreground mb-3">
          {t('paymentDetails.transactionDetails')}
        </h3>
        <div className="bg-muted/50 rounded-lg divide-y divide-border">
          <div className="flex justify-between items-center px-4 py-3">
            <span className="text-list-item-subtitle">{t('paymentDetails.paymentDate')}</span>
            <span className="text-list-item-subtitle text-muted-foreground">
              {formattedDate}
            </span>
          </div>
          <div className="flex justify-between items-center px-4 py-3">
            <span className="text-list-item-subtitle">{t('paymentDetails.time')}</span>
            <span className="text-list-item-subtitle text-muted-foreground">
              {formattedTime}
            </span>
          </div>
          <div className="flex justify-between items-center px-4 py-3">
            <span className="text-list-item-subtitle">{t('paymentDetails.provider')}</span>
            <span className="text-list-item-subtitle text-muted-foreground">
              {providerLabel}
            </span>
          </div>
          <div className="flex justify-between items-center px-4 py-3">
            <span className="text-list-item-subtitle">{t('paymentDetails.currency')}</span>
            <span className="text-list-item-subtitle text-muted-foreground">
              {(payment.attributes.currency ?? '').toUpperCase()}
            </span>
          </div>
          {formattedBillingPeriod && (
            <div className="flex justify-between items-center px-4 py-3">
              <span className="text-list-item-subtitle">
                {t('paymentDetails.billingPeriod')}
              </span>
              <span className="text-list-item-subtitle text-muted-foreground">
                {formattedBillingPeriod}
              </span>
            </div>
          )}
          {billingPeriod && (
            <div className="flex justify-between items-center px-4 py-3">
              <span className="text-list-item-subtitle">
                {t('paymentDetails.subscriptionCycle')}
              </span>
              <span className="text-list-item-subtitle text-muted-foreground">
                {t(`paymentDetails.cycle.${billingPeriod}`)}
              </span>
            </div>
          )}
          {refundedAmountFormatted && (
            <div className="flex justify-between items-center px-4 py-3">
              <span className="text-list-item-subtitle">
                {t('paymentDetails.refundedAmount')}
              </span>
              <span className="text-list-item-subtitle text-warning">
                {refundedAmountFormatted}
              </span>
            </div>
          )}
          {showFailureReason && (
            <div className="flex justify-between items-start gap-3 px-4 py-3">
              <span className="text-list-item-subtitle shrink-0">
                {t('paymentDetails.failureReason')}
              </span>
              <span className="text-list-item-subtitle text-muted-foreground text-right">
                {failureReason}
              </span>
            </div>
          )}
        </div>
        {payment.attributes.status === 'requires_action' && (
          <p className="text-card-meta text-muted-foreground mt-2">
            {t('paymentDetails.requiresActionHint')}
          </p>
        )}
      </div>

      {/* Support */}
      <div>
        <h3 className="text-section-eyebrow text-muted-foreground mb-3">
          {t('paymentDetails.support')}
        </h3>
        <div className="bg-muted/50 rounded-lg divide-y divide-border">
          <div className="px-4 py-3">
            <p className="text-list-item-subtitle mb-0.5">{t('paymentDetails.paymentId')}</p>
            <p className="text-card-meta text-muted-foreground font-mono break-all">
              {payment.id}
            </p>
          </div>
          {transactionId && (
            <div className="px-4 py-3">
              <p className="text-list-item-subtitle mb-0.5">
                {t('paymentDetails.transactionId')}
              </p>
              <p className="text-card-meta text-muted-foreground font-mono break-all">
                {transactionId}
              </p>
            </div>
          )}
          <button
            onClick={handleCopyPaymentId}
            className="w-full flex items-center gap-2 px-4 py-3 text-primary hover:bg-muted/80 transition-colors"
          >
            {isCopied ? (
              <Check className="w-4 h-4" />
            ) : (
              <Copy className="w-4 h-4" />
            )}
            <span className="text-list-item-subtitle">
              {isCopied
                ? t('paymentDetails.copied')
                : t('paymentDetails.copyPaymentId')}
            </span>
          </button>
        </div>
      </div>
    </div>
  );

  // Mobile: Drawer (bottom) with proper animations
  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={onOpenChange}>
        <DrawerContent className="max-h-[90vh]">
          {headerContent}
          <div className="overflow-y-auto">{bodyContent}</div>
        </DrawerContent>
      </Drawer>
    );
  }

  // Desktop: centered Dialog
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-md max-h-[85vh] overflow-y-auto p-0"
        showCloseButton={false}
      >
        {headerContent}
        {bodyContent}
      </DialogContent>
    </Dialog>
  );
};
